import { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Linkedin, Facebook, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { NAV_LINKS } from "@/lib/brand";

export function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("You're subscribed. Welcome to the circle.");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-espresso text-ivory grain relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 relative z-10">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <h2 className="font-serif text-5xl md:text-6xl leading-none">Let's talk.</h2>
            <p className="mt-6 text-ivory/70 max-w-sm">
              Elevated HR solutions and personalized career services, delivered 100% virtually.
            </p>
            <Link
              to="/book"
              data-testid="footer-book"
              className="inline-flex items-center gap-2 mt-8 bg-champagne text-espresso rounded-full px-7 py-3 font-medium hover:bg-ivory transition-colors duration-300"
            >
              Book a Consultation <ArrowRight size={18} />
            </Link>
          </div>

          <div className="lg:col-span-3">
            <h4 className="text-sm uppercase tracking-[0.2em] text-gold mb-5">Explore</h4>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-ivory/75 hover:text-ivory transition-colors">{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <h4 className="text-sm uppercase tracking-[0.2em] text-gold mb-5">Stay in the know</h4>
            <p className="text-ivory/70 text-sm mb-4">Career & HR insights, straight to your inbox.</p>
            <form onSubmit={subscribe} className="flex gap-2" data-testid="footer-newsletter-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                data-testid="footer-newsletter-input"
                className="flex-1 bg-ivory/10 border border-ivory/20 rounded-full px-5 py-3 text-ivory placeholder:text-ivory/40 focus:outline-none focus:border-champagne"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="footer-newsletter-submit"
                className="bg-champagne text-espresso rounded-full px-5 py-3 font-medium hover:bg-ivory transition-colors disabled:opacity-60"
              >
                Join
              </button>
            </form>
            <div className="flex gap-4 mt-8">
              <a href="#" aria-label="Instagram" className="text-ivory/70 hover:text-gold transition-colors"><Instagram size={20} /></a>
              <a href="#" aria-label="LinkedIn" className="text-ivory/70 hover:text-gold transition-colors"><Linkedin size={20} /></a>
              <a href="#" aria-label="Facebook" className="text-ivory/70 hover:text-gold transition-colors"><Facebook size={20} /></a>
            </div>
          </div>
        </div>

        <div className="border-t border-ivory/15 mt-16 pt-8 flex flex-col md:flex-row justify-between gap-4 text-sm text-ivory/55">
          <p>© {new Date().getFullYear()} people, redefined. · Amani Talent Agency LLC. All services provided virtually.</p>
          <div className="flex gap-6">
            <Link to="/legal/privacy" className="hover:text-ivory transition-colors">Privacy</Link>
            <Link to="/legal/terms" className="hover:text-ivory transition-colors">Terms</Link>
            <Link to="/legal/refund" className="hover:text-ivory transition-colors">Refund & Cancellation</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
